"use client"

import Link from "next/link"
import { useEffect, useState } from "react"
import { Clock } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import type { Article } from "@/lib/types"
import { formatDaysSinceCreated, formatDaysSinceCreatedTamil } from "@/lib/format-days-since"

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://dev.oxygentamil.com/api"

/** Health / ஆரோக்கியம் category — sidebar teaser above சிறப்புக்கட்டுரைகள் */
const HEALTH_CATEGORY_ID = 10
const HEALTH_SECTION_TITLE = "ஆரோக்கியம்"

/** First N viral/special items; rest go to “அதிகம் படிக்கப்பட்டவை”. */
const SPECIAL_ARTICLES_LIMIT = 5

const healthCardStyle = {
  background: "#ecf8ef",
  borderColor: "#5cb85c",
  titleColor: "#143d28",
  metaColor: "#2d6a4f",
} as const

function HealthArticleCard({ article }: { article: Article }) {
  const insetSrc =
    article.input_images && article.input_images.length > 0
      ? article.input_images[0]
      : null
  const mainSrc = article.featured_image

  return (
    <Link href={`/article/${article.slug}`} className="block group">
      <div
        className="rounded-[10px] border p-3 shadow-sm transition-shadow hover:shadow-md"
        style={{
          backgroundColor: healthCardStyle.background,
          borderColor: healthCardStyle.borderColor,
        }}
      >
        <div className="relative mb-3 overflow-hidden rounded-lg bg-white/40">
          {insetSrc && (
            <div
              className="absolute left-2 top-2 z-10 h-14 w-14 overflow-hidden rounded-full border-2 border-white shadow-md ring-1 ring-green-600/20"
              aria-hidden
            >
              <img
                src={insetSrc}
                alt=""
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          )}
          <div className="aspect-[5/3] w-full overflow-hidden rounded-lg bg-muted/30">
            {mainSrc ? (
              <img
                src={mainSrc}
                alt={article.title}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
            ) : (
              <div className="flex h-full min-h-[120px] items-center justify-center text-xs text-muted-foreground">
                No image
              </div>
            )}
          </div>
        </div>
        <h4
          className="line-clamp-2 font-bold leading-snug"
          style={{ color: healthCardStyle.titleColor, fontSize: "15px" }}
          title={article.title.trim()}
        >
          {article.title.trim()}
        </h4>
        {article.created_at && (
          <p
            className="mt-1.5 text-xs font-medium"
            style={{ color: healthCardStyle.metaColor }}
          >
            {formatDaysSinceCreatedTamil(article.created_at)}
          </p>
        )}
      </div>
    </Link>
  )
}

function HealthCategorySection({
  articles,
  emptyText,
}: {
  articles: Article[]
  emptyText: string
}) {
  return (
    <div className="pb-3">
      <div className="px-3 pt-3 pb-0">
        <div
          className="flex items-center rounded-md"
          style={{
            backgroundColor: "#4a4a4a",
            borderRadius: "6px",
            padding: "8px 12px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              backgroundColor: "#D00501",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
          <span
            className="font-semibold"
            style={{
              fontSize: "14px",
              color: "#FFFFFF",
              marginLeft: "8px",
            }}
          >
            {HEALTH_SECTION_TITLE}
          </span>
        </div>
      </div>
      <CardContent className="px-3 pt-3">
        {articles.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {articles.map((article) => (
              <HealthArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </CardContent>
    </div>
  )
}

function normalizeArticleList(data: unknown): Article[] {
  const d = data as {
    items?: unknown
    data?: unknown
    results?: unknown
    articles?: unknown
  } | null
  const list =
    (Array.isArray(data) && data) ||
    d?.items ||
    d?.data ||
    d?.results ||
    d?.articles ||
    []
  return (Array.isArray(list) ? list : []).map((a: Article) => ({
    ...a,
    id: String(a.id),
  }))
}

interface ViralNewsSidebarProps {
  importantArticles?: Article[]
  viralArticles?: Article[]
  onLoadMoreImportant?: () => void
  hasMoreImportant?: boolean
}

function SidebarSection({
  title,
  articles,
  emptyText,
  onScrollEnd,
}: {
  title: string
  articles?: Article[]
  emptyText: string
  onScrollEnd?: () => void
}) {
  const safeArticles = articles || []
  const articlesToRender = safeArticles
  return (
    <div className="pb-3 last:pb-0">
      <div className="px-3 pt-3 pb-0">
        <div
          className="flex items-center rounded-md"
          style={{
            backgroundColor: "#4a4a4a",
            borderRadius: "6px",
            padding: "8px 12px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              backgroundColor: "#D00501",
              borderTopLeftRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
          <span
            className="font-semibold"
            style={{
              fontSize: "14px",
              color: "#FFFFFF",
              marginLeft: "8px",
            }}
          >
            {title}
          </span>
        </div>
      </div>

      <CardContent className="px-3 pt-3">
        {safeArticles.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <>
            <div className="space-y-3">
              {articlesToRender.map((article) => (
                <Link key={article.id} href={`/article/${article.slug}`} className="block group">
                  <div className="flex gap-2 pb-2 border-b border-gray-200 last:border-b-0">
                    {/* Thumbnail */}
                    <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden rounded">
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <span className="text-muted-foreground text-xs">No Image</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h4
                        style={{
                          position: "relative",
                          fontSize: "14px",
                          lineHeight: "20px",
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                        className="group-hover:text-primary transition-colors mb-1"
                        title={article.title}
                      >
                        {article.title}
                      </h4>
                      {article.created_at && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3 shrink-0" />
                          <span>{formatDaysSinceCreated(article.created_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {onScrollEnd && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={onScrollEnd}
                  className="px-3 py-1 text-xs font-medium border rounded-md hover:bg-gray-100"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </div>
  )
}

export function ViralNewsSidebar({
  importantArticles,
  viralArticles,
  onLoadMoreImportant,
  hasMoreImportant = false,
}: ViralNewsSidebarProps) {
  const safeImportant = importantArticles || []
  const safeViral = viralArticles || []
  /** Home: viral list is longer than the loaded “important” prefix. Article: same length → prefer important so Load more can extend. */
  const useViralAsMaster = safeViral.length > safeImportant.length
  const orderedViralFeed = useViralAsMaster ? safeViral : safeImportant
  const specialArticles = orderedViralFeed.slice(0, SPECIAL_ARTICLES_LIMIT)
  const mostReadArticles = orderedViralFeed.slice(SPECIAL_ARTICLES_LIMIT)
  const showSpecialLoadMore =
    Boolean(
      onLoadMoreImportant &&
        hasMoreImportant &&
        !useViralAsMaster,
    )
  const [healthArticles, setHealthArticles] = useState<Article[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/articles?category_id=${HEALTH_CATEGORY_ID}&limit=2`,
        )
        if (!res.ok || cancelled) return
        const json: unknown = await res.json()
        if (cancelled) return
        setHealthArticles(normalizeArticleList(json).slice(0, 2))
      } catch {
        if (!cancelled) setHealthArticles([])
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Card 
      className="py-0" 
      style={{ 
        display: "block",
        position: "relative",
        backgroundColor: "var(--viral-news-box-bg-color, #FFFFFF)",
        background: "#fff",
        borderRadius: "5px",
        boxShadow: "1px 1px 5px rgba(0, 0, 0, .12)"
      }}
    >
      <a
        href="https://shreevarma.online/products/maharaja-sg-capsule"
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="mx-3 mt-3 mb-1 block overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm outline-none transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Advertisement — opens Shreevarma product page in a new tab"
      >
        <img
          src="/advertisement.jpeg"
          alt="Advertisement"
          className="h-auto w-full object-cover"
          loading="lazy"
        />
      </a>
      <HealthCategorySection
        articles={healthArticles}
        emptyText="No articles in this category"
      />
      <SidebarSection
        title="சிறப்புக்கட்டுரைகள்"
        articles={specialArticles}
        emptyText="No important news available"
        onScrollEnd={showSpecialLoadMore ? onLoadMoreImportant : undefined}
      />
      <SidebarSection
        title="அதிகம் படிக்கப்பட்டவை"
        articles={mostReadArticles}
        emptyText="No viral news available"
      />
    </Card>
  )
}

